import FloorPlanNavigation from './FloorPlanNavigation.jsx';
import FloorPlanCategoryPanel from './FloorPlanCategoryPanel.jsx';
import BuildPanel from './BuildPanel.jsx';
import FurnishPanel from './FurnishPanel.jsx';
import SettingsPanel from './SettingsPanel.jsx';
import HelpPanel from './HelpPanel.jsx';
import AssetLibraryPanel from './AssetLibraryPanel.jsx';
import CanvasToolbar from './CanvasToolbar.jsx';
import ViewModeSwitcher from './ViewModeSwitcher.jsx';
import VisibleHeightControl from './VisibleHeightControl.jsx';

/** @typedef {'build' | 'furnish' | 'settings' | 'help'} WorkspaceCategory */

const CATEGORY_TITLES = {
  build: 'Build',
  furnish: 'Furnish',
  settings: 'Settings',
  help: 'Help',
};

/**
 * @param {{
 *   activeCategory: WorkspaceCategory;
 *   onCategoryChange: (category: WorkspaceCategory) => void;
 *   isSubcategoryPanelOpen?: boolean;
 *   onCollapseSubcategoryPanel?: () => void;
 *   activeBuildTool: string | null;
 *   onBuildToolChange: (tool: string | null) => void;
 *   libraryMode: 'doors' | 'windows' | 'structures' | null;
 *   onOpenLibrary: (mode: 'doors' | 'windows' | 'structures') => void;
 *   onLibraryBack: () => void;
 *   onLibrarySelectItem: (item: import('../../../lib/floorPlanOpeningCatalog.js').FloorPlanOpeningCatalogItem) => void;
 *   buildUploadContent?: import('react').ReactNode;
 *   showBuildUpload?: boolean;
 *   snapToGrid: boolean;
 *   onSnapToGridChange: (value: boolean) => void;
 *   snapGridMeters: number;
 *   onSnapGridMetersChange: (value: number) => void;
 *   snapTolerancePx: number;
 *   onSnapToleranceChange: (value: number) => void;
 *   wallThicknessMeters: number;
 *   onWallThicknessChange: (value: number) => void;
 *   thicknessDirection: 'inside' | 'outside';
 *   onThicknessDirectionChange: (value: 'inside' | 'outside') => void;
 *   onClear: () => void;
 *   viewMode: '2d' | '3d';
 *   onViewModeChange: (mode: '2d' | '3d') => void;
 *   canUse3D: boolean;
 *   visibleHeightM: number;
 *   maxVisibleHeightM: number;
 *   onVisibleHeightChange: (value: number) => void;
 *   onUndo?: () => void;
 *   onRedo?: () => void;
 *   onZoomIn?: () => void;
 *   onZoomOut?: () => void;
 *   onFit?: () => void;
 *   canUndo?: boolean;
 *   canRedo?: boolean;
 *   children: import('react').ReactNode;
 * }} props
 */
function FloorPlanWorkspace({
  activeCategory,
  onCategoryChange,
  isSubcategoryPanelOpen = true,
  onCollapseSubcategoryPanel,
  activeBuildTool,
  onBuildToolChange,
  libraryMode,
  onOpenLibrary,
  onLibraryBack,
  onLibrarySelectItem,
  buildUploadContent,
  showBuildUpload = true,
  snapToGrid,
  onSnapToGridChange,
  snapGridMeters,
  onSnapGridMetersChange,
  snapTolerancePx,
  onSnapToleranceChange,
  wallThicknessMeters,
  onWallThicknessChange,
  thicknessDirection,
  onThicknessDirectionChange,
  onClear,
  viewMode,
  onViewModeChange,
  canUse3D,
  visibleHeightM,
  maxVisibleHeightM,
  onVisibleHeightChange,
  onUndo,
  onRedo,
  onZoomIn,
  onZoomOut,
  onFit,
  canUndo,
  canRedo,
  children,
}) {
  const showCategoryPanel = !libraryMode;
  const panelOpen = showCategoryPanel && isSubcategoryPanelOpen;
  const workspaceClass = [
    'floor-plan-workspace',
    panelOpen ? 'floor-plan-workspace--panel-open' : '',
    !isSubcategoryPanelOpen && showCategoryPanel ? 'floor-plan-workspace--subcategory-collapsed' : '',
    libraryMode ? 'floor-plan-workspace--library' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const renderCategoryContent = () => {
    switch (activeCategory) {
      case 'furnish':
        return <FurnishPanel />;
      case 'settings':
        return (
          <SettingsPanel
            snapToGrid={snapToGrid}
            onSnapToGridChange={onSnapToGridChange}
            snapGridMeters={snapGridMeters}
            onSnapGridMetersChange={onSnapGridMetersChange}
            snapTolerancePx={snapTolerancePx}
            onSnapToleranceChange={onSnapToleranceChange}
            wallThicknessMeters={wallThicknessMeters}
            onWallThicknessChange={onWallThicknessChange}
            thicknessDirection={thicknessDirection}
            onThicknessDirectionChange={onThicknessDirectionChange}
            onClear={onClear}
          />
        );
      case 'help':
        return <HelpPanel />;
      case 'build':
      default:
        return (
          <BuildPanel
            activeBuildTool={activeBuildTool}
            onBuildToolChange={onBuildToolChange}
            onOpenLibrary={onOpenLibrary}
            uploadContent={buildUploadContent}
            showUpload={showBuildUpload}
          />
        );
    }
  };

  return (
    <div className={workspaceClass}>
      {children}

      {libraryMode ? (
        <AssetLibraryPanel mode={libraryMode} onBack={onLibraryBack} onSelectItem={onLibrarySelectItem} />
      ) : (
        <>
          <FloorPlanNavigation activeCategory={activeCategory} onCategoryChange={onCategoryChange} />
          {showCategoryPanel ? (
            <FloorPlanCategoryPanel
              title={CATEGORY_TITLES[activeCategory] || 'Build'}
              collapsible
              onCollapse={onCollapseSubcategoryPanel}
              className={isSubcategoryPanelOpen ? '' : 'floor-plan-workspace-panel--hidden'}
            >
              {renderCategoryContent()}
            </FloorPlanCategoryPanel>
          ) : null}
        </>
      )}

      <CanvasToolbar
        onUndo={onUndo}
        onRedo={onRedo}
        onZoomIn={onZoomIn}
        onZoomOut={onZoomOut}
        onFit={onFit}
        canUndo={canUndo}
        canRedo={canRedo}
      />

      <ViewModeSwitcher mode={viewMode} onChange={onViewModeChange} disabled={!canUse3D} />

      <VisibleHeightControl
        visibleHeightM={visibleHeightM}
        maxVisibleHeightM={maxVisibleHeightM}
        onVisibleHeightChange={onVisibleHeightChange}
        hidden={viewMode !== '3d'}
      />
    </div>
  );
}

export default FloorPlanWorkspace;
